import React, { ReactNode, useMemo, useState } from 'react'
import { Box, ButtonBase, Checkbox, IconButton, OutlinedInput, Popover } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import { __t } from '../../../../i18n/t'
import { norm } from '../format'
import { DataState } from '../types'
import { Callout, FieldError } from './basics'
import { OutlineButton, TextButton, inputSx } from './controls'
import { C, focusRing } from './tokens'

export type PickerKind = 'stations' | 'tags' | 'groups' | 'cities'

/** Textos de cada seletor. Função (não constante): o idioma troca sem recarregar. */
function copyFor(kind: PickerKind) {
  switch (kind) {
    case 'stations':
      return {
        trigger: (n: number, total: number) => (total === 1 ? __t('1 de 1 posto marcado') : n === 1 ? __t('1 de %{total} postos marcado', { total }) : __t('%{n} de %{total} postos marcados', { n, total })),
        empty: __t('Clique para buscar e escolher os postos'), placeholder: __t('Buscar posto…'), search: __t('Buscar posto'), dialog: __t('Escolher postos'),
        summary: (total: number, n: number) => (total === 1 ? (n === 1 ? __t('1 posto · 1 marcado') : __t('1 posto · 0 marcados')) : n === 1 ? __t('%{total} postos · 1 marcado', { total }) : __t('%{total} postos · %{n} marcados', { total, n })),
        found: (n: number) => (n === 1 ? __t('1 encontrado') : __t('%{n} encontrados', { n })),
      }
    case 'tags':
      return {
        trigger: (n: number, total: number) => (total === 1 ? __t('1 de 1 etiqueta marcada') : n === 1 ? __t('1 de %{total} etiquetas marcada', { total }) : __t('%{n} de %{total} etiquetas marcadas', { n, total })),
        empty: __t('Clique para buscar e escolher etiquetas'), placeholder: __t('Buscar etiqueta…'), search: __t('Buscar etiqueta'), dialog: __t('Escolher etiquetas'),
        summary: (total: number, n: number) => (total === 1 ? (n === 1 ? __t('1 etiqueta · 1 marcada') : __t('1 etiqueta · 0 marcadas')) : n === 1 ? __t('%{total} etiquetas · 1 marcada', { total }) : __t('%{total} etiquetas · %{n} marcadas', { total, n })),
        found: (n: number) => (n === 1 ? __t('1 encontrada') : __t('%{n} encontradas', { n })),
      }
    case 'groups':
      return {
        trigger: (n: number, total: number) => (total === 1 ? __t('1 de 1 grupo marcado') : n === 1 ? __t('1 de %{total} grupos marcado', { total }) : __t('%{n} de %{total} grupos marcados', { n, total })),
        empty: __t('Clique para escolher grupos'), placeholder: __t('Buscar grupo…'), search: __t('Buscar grupo'), dialog: __t('Escolher grupos'),
        summary: (total: number, n: number) => (total === 1 ? (n === 1 ? __t('1 grupo · 1 marcado') : __t('1 grupo · 0 marcados')) : n === 1 ? __t('%{total} grupos · 1 marcado', { total }) : __t('%{total} grupos · %{n} marcados', { total, n })),
        found: (n: number) => (n === 1 ? __t('1 encontrado') : __t('%{n} encontrados', { n })),
      }
    default:
      return {
        trigger: (n: number, total: number) => (total === 1 ? __t('1 de 1 cidade marcada') : n === 1 ? __t('1 de %{total} cidades marcada', { total }) : __t('%{n} de %{total} cidades marcadas', { n, total })),
        empty: __t('Clique para escolher cidades'), placeholder: __t('Buscar cidade…'), search: __t('Buscar cidade'), dialog: __t('Escolher cidades'),
        summary: (total: number, n: number) => (total === 1 ? (n === 1 ? __t('1 cidade · 1 marcada') : __t('1 cidade · 0 marcadas')) : n === 1 ? __t('%{total} cidades · 1 marcada', { total }) : __t('%{total} cidades · %{n} marcadas', { total, n })),
        found: (n: number) => (n === 1 ? __t('1 encontrada') : __t('%{n} encontradas', { n })),
      }
  }
}

export interface PickerItem { id: string; name: string; meta?: string | null }

const MAX_CHIPS = 8
const MAX_ROWS = 200

const wrapSx = { display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 520, width: '100%' } as const
const triggerSx = {
  width: '100%', minHeight: 44, px: 1.5, py: 0.75, gap: 1, justifyContent: 'flex-start', textAlign: 'left', borderRadius: '8px', bgcolor: '#fff',
  '&.Mui-focusVisible': focusRing,
} as const
const triggerLabelSx = { flex: 1, minWidth: 0, fontSize: 15, overflowWrap: 'anywhere' } as const
const popoverPaperSx = {
  mt: 0.5, maxWidth: 'calc(100vw - 16px)', borderRadius: '12px', boxShadow: '0 12px 32px rgba(30,41,59,.16)', border: `1px solid ${C.radioBorder}`, display: 'flex', flexDirection: 'column',
} as const
const searchBoxSx = { p: 1.25, borderBottom: `1px solid ${C.lineSoft}` } as const
const toolbarSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pl: 1.75, pr: 0.5, borderBottom: `1px solid ${C.lineSoft}`, fontSize: 13, color: C.muted } as const
const listSx = { maxHeight: 'min(264px, 45vh)', overflowY: 'auto' } as const
const rowSx = { display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, minHeight: 44, cursor: 'pointer', '&:hover': { bgcolor: C.surface }, '&:has(input:focus-visible)': { outline: `2px solid ${C.violet}`, outlineOffset: '-2px' } } as const
const rowOnSx = { ...rowSx, bgcolor: C.violetSoft } as const
const rowOffSx = { ...rowSx, bgcolor: '#fff' } as const
const checkSx = { p: 0, color: C.inputBorder, '&.Mui-checked': { color: C.violet }, '& .MuiSvgIcon-root': { fontSize: 22 } } as const
const rowNameSx = { flex: 1, minWidth: 0, fontSize: 15, color: C.ink, overflowWrap: 'anywhere' } as const
const rowMetaSx = { fontSize: 13, color: C.muted, whiteSpace: 'nowrap' } as const
const moreSx = { p: '12px 14px', fontSize: 13, color: C.muted } as const
const emptySx = { p: '16px 14px', fontSize: 14, color: C.muted } as const
const footerSx = { p: 1.25, borderTop: `1px solid ${C.lineSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 } as const
const doneSx = { height: 40, px: 2, borderRadius: '8px', bgcolor: C.green, color: C.ink, fontSize: 14, fontWeight: 700, flex: 'none', '&:hover': { bgcolor: C.greenHover }, '&.Mui-focusVisible': focusRing } as const
const chipsSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' } as const
const chipSx = {
  display: 'inline-flex', alignItems: 'center', minHeight: 40, maxWidth: '100%', overflowWrap: 'anywhere', pl: 1.5, pr: 0, borderRadius: '20px',
  bgcolor: C.chipBg, border: `1px solid ${C.chipBorder}`, fontSize: 14, color: C.ink,
} as const
const chipCloseSx = { width: 40, height: 40, '&.Mui-focusVisible': focusRing } as const

const borderOf = (open: boolean, invalid: boolean) => `1px solid ${open ? C.violet : invalid ? C.red : C.inputBorder}`

/**
 * Seletor de vários itens com busca, "Marcar todos" e chips (postos, etiquetas, grupos, cidades).
 * Abre num popover ancorado no botão; Esc fecha e o foco volta para o botão. Ao tirar um chip ou limpar, o foco
 * volta para o botão do seletor (o chip some e o foco não pode cair no fim da página).
 * `state` erro: lista que não carregou, com "Tentar de novo" (em vez de um "Nada para escolher ainda" que mente).
 */
export const MultiPicker: React.FC<{
  id: string
  kind: PickerKind
  items: PickerItem[]
  selected: string[]
  onChange: (ids: string[]) => void
  error?: string
  errorId?: string
  footNote?: string
  state?: DataState
  onRetry?: () => void
  /** Aviso no lugar da lista quando a rede não tem nenhum item (ex.: como criar uma etiqueta). */
  emptyNote?: ReactNode
}> = ({ id, kind, items, selected, onChange, error, errorId, footNote, state = DataState.ready, onRetry, emptyNote }) => {
  const copy = copyFor(kind)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [q, setQ] = useState('')
  const open = Boolean(anchor)
  const panelId = `${id}-panel`
  const loading = state === DataState.loading
  const failed = state === DataState.error
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const found = useMemo(() => (q ? items.filter(x => norm(x.name).includes(norm(q))) : items), [items, q])
  const allOn = found.length > 0 && found.every(x => selectedSet.has(x.id))
  const chips = items.filter(x => selectedSet.has(x.id))
  const close = () => { setAnchor(null); setQ('') }
  const toggle = (itemId: string) => onChange(selectedSet.has(itemId) ? selected.filter(v => v !== itemId) : [...selected, itemId])
  const toggleAll = () => onChange(allOn ? selected.filter(v => !found.some(x => x.id === v)) : Array.from(new Set([...selected, ...found.map(x => x.id)])))
  const invalid = !!error
  const focusTrigger = () => { window.setTimeout(() => document.getElementById(`${id}-btn`)?.focus(), 0) }
  const triggerText = loading ? __t('Carregando…') : selected.length ? copy.trigger(selected.length, items.length) : copy.empty

  if (failed) {
    return (
      <Box id={id} sx={wrapSx}>
        <Callout tone="error" role="alert">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
            <span>{__t('Não foi possível carregar esta lista.')}</span>
            {onRetry ? <OutlineButton onClick={onRetry} sx={{ height: 40, px: 1.75, fontSize: 14 }}>{__t('Tentar de novo')}</OutlineButton> : null}
          </Box>
        </Callout>
        {invalid && errorId ? <FieldError id={errorId}>{error}</FieldError> : null}
      </Box>
    )
  }

  return (
    <Box id={id} sx={wrapSx}>
      <ButtonBase
        id={`${id}-btn`}
        onClick={e => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        focusRipple={false}
        sx={{ ...triggerSx, border: borderOf(open, invalid) }}
      >
        <SearchIcon aria-hidden sx={{ fontSize: 20, color: C.quiet, flex: 'none' }} />
        <Box component="span" sx={{ ...triggerLabelSx, color: selected.length ? C.ink : C.muted }}>{triggerText}</Box>
        {open ? <ExpandLessIcon aria-hidden sx={{ color: C.muted }} /> : <ExpandMoreIcon aria-hidden sx={{ color: C.muted }} />}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { id: panelId, role: 'dialog', 'aria-label': copy.dialog, sx: { ...popoverPaperSx, width: Math.max(anchor?.offsetWidth ?? 0, 300) } } as never }}
      >
        <Box sx={searchBoxSx}>
          <Box component="label" htmlFor={`${id}-q`} sx={visuallyHidden}>{copy.search}</Box>
          <OutlinedInput
            id={`${id}-q`} type="search" size="small" fullWidth autoFocus value={q} placeholder={copy.placeholder}
            onChange={e => setQ(e.target.value)} slotProps={{ input: { autoComplete: 'off' } as never }} sx={inputSx()}
          />
        </Box>
        <Box sx={toolbarSx}>
          <span aria-live="polite">{q ? copy.found(found.length) : copy.summary(items.length, selected.length)}</span>
          <TextButton onClick={toggleAll} disabled={!found.length}>
            {allOn ? (q ? __t('Desmarcar encontrados') : __t('Desmarcar todos')) : (q ? __t('Marcar encontrados') : __t('Marcar todos'))}
          </TextButton>
        </Box>
        <Box sx={listSx}>
          {found.slice(0, MAX_ROWS).map(x => {
            const on = selectedSet.has(x.id)
            return (
              <Box key={x.id} component="label" sx={on ? rowOnSx : rowOffSx}>
                <Checkbox checked={on} onChange={() => toggle(x.id)} disableRipple sx={checkSx} />
                <Box component="span" sx={rowNameSx}>{x.name}</Box>
                {x.meta ? <Box component="span" sx={rowMetaSx}>{x.meta}</Box> : null}
              </Box>
            )
          })}
          {found.length > MAX_ROWS ? (
            <Box sx={moreSx}>{__t('Mostrando os primeiros %{n}. Use a busca para achar os outros.', { n: MAX_ROWS })}</Box>
          ) : null}
          {!found.length ? (
            <Box sx={emptySx}>
              {q ? __t('Nada encontrado para “%{busca}”.', { busca: q }) : (emptyNote ?? __t('Nada para escolher ainda.'))}
            </Box>
          ) : null}
        </Box>
        <Box sx={footerSx}>
          <Box component="span" sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.35 }}>{footNote}</Box>
          <ButtonBase onClick={close} sx={doneSx}>
            {__t('Pronto')}
          </ButtonBase>
        </Box>
      </Popover>

      {selected.length ? (
        <Box sx={chipsSx}>
          {chips.slice(0, MAX_CHIPS).map(x => (
            <Box key={x.id} sx={chipSx}>
              {x.name}
              <IconButton
                onClick={() => { toggle(x.id); focusTrigger() }}
                aria-label={__t('Remover %{nome}', { nome: x.name })}
                size="small"
                sx={chipCloseSx}
              >
                <CloseIcon aria-hidden sx={{ fontSize: 16, color: C.muted }} />
              </IconButton>
            </Box>
          ))}
          {chips.length > MAX_CHIPS ? <TextButton onClick={() => document.getElementById(`${id}-btn`)?.click()}>{__t('e mais %{n}', { n: chips.length - MAX_CHIPS })}</TextButton> : null}
          <TextButton onClick={() => { onChange([]); focusTrigger() }} color={C.muted} underline>{__t('Limpar')}</TextButton>
        </Box>
      ) : null}
      {invalid && errorId ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Box>
  )
}
